import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbDispatch } from './index';
import { GetOperationalContacts } from './operationalContactsSlice';
import { PbRequest } from './pbRequest';

interface UserAddress {
    id?: number;
    first_name: string;
    last_name: string;
    street: string;
    housenumber: string;
    postalcode: string;
    city: string;
    country_id: number;
    email: string;
    phone: string;
    description?: string;
}

type MansionType =
    | 'campsite'
    | 'clinic'
    | 'serviced_apartment'
    | 'micro_living'
    | 'multi_family_house'
    | 'student_housing'
    | 'senior_living'
    | 'other_or_not_known'
    | 'hostel'
    | 'hotel'
    | 'staff_housing'
    | 'truck_stop'
    | 'vacation_apartment_or_home'
    | 'fitters_flat'
    | 'public_laundrette';

export interface Mansion {
    id?: number;
    partner_id?: number;
    address: UserAddress;
    housing_units: number;
    num_of_machines: number;
    mansion_type: MansionType;
    update_all_locations?: boolean;
    num_of_locations?: number;
    geo_location?: CoordinatesApiDto;
    num_of_operational_contacts?: number;
}

export interface MansionCreation extends Mansion {
    operational_contact_ids: number[];
}

interface CoordinatesApiDto {
    gps_lat: number;
    gps_lon: number;
}

interface Coordinates {
    lat: number;
    lon: number;
}

interface MansionsStore {
    mansions: Mansion[];
    mansionsById: { [key: string]: Mansion };
    mansionCoordsById: { [key: number]: Coordinates };
}

const initialState: MansionsStore = {
    mansions: [],
    mansionsById: {},
    mansionCoordsById: {},
};

const mansionsSlice = createSlice({
    name: 'mansions',
    initialState,
    reducers: {
        setMansions(state: MansionsStore, action: PayloadAction<Mansion[]>) {
            state.mansions = action.payload;
        },
        setMansionsById(
            state: MansionsStore,
            action: PayloadAction<{ [key: string]: Mansion }>
        ) {
            state.mansionsById = action.payload;
        },
        setMansionCoordsById(
            state: MansionsStore,
            action: PayloadAction<{ [key: number]: Coordinates }>
        ) {
            state.mansionCoordsById = action.payload;
        },
        setSingleMansion(state: MansionsStore, action: PayloadAction<Mansion>) {
            const mansion: Mansion = action.payload;
            if (mansion.id) {
                state.mansionsById[mansion.id] = mansion;
            }
        },
    },
});

const { setMansions, setMansionsById, setSingleMansion, setMansionCoordsById } =
    mansionsSlice.actions;

export function LoadMansions() {
    return async (dispatch: PbDispatch) => {
        try {
            const mansions =
                await processRequestForAccountingAreaWithReplacement<Mansion[]>(
                    dispatch,
                    `${API}/mansions/me/${ACCOUNTING_AREA_ID_MARKER}`,
                    { method: 'GET' },
                    PbRequest.LOAD_MANSIONS
                );
            dispatch(setMansions(mansions));

            const mansionsById = {};
            const mansionCoordsById = {};
            mansions.forEach((m) => {
                mansionsById[m.id] = m;
                mansionCoordsById[m.id] = {
                    lat: m.geo_location.gps_lat,
                    lon: m.geo_location.gps_lon,
                };
            });
            dispatch(setMansionCoordsById(mansionCoordsById));
            dispatch(setMansionsById(mansionsById));
        } catch {}
    };
}

export function CreateMansion(
    mansionBlueprint: MansionCreation,
    callback?: () => void
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                body: JSON.stringify(mansionBlueprint),
            };
            const createdMansion =
                await processRequestForAccountingAreaWithReplacement<Mansion>(
                    dispatch,
                    `${API}/mansions/${ACCOUNTING_AREA_ID_MARKER}`,
                    request,
                    PbRequest.CREATE_MANSION
                );

            dispatch(setSingleMansion(createdMansion));
            await LoadMansions()(dispatch);
            await GetOperationalContacts()(dispatch);

            callback?.();
        } catch {}
    };
}

export function RemoveMansion(mansionId: number, callback?: () => void) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'DELETE',
            };
            const deleteMansion =
                await processRequestForAccountingAreaWithReplacement<Mansion>(
                    dispatch,
                    `${API}/mansions/${mansionId}/${ACCOUNTING_AREA_ID_MARKER}`,
                    request,
                    PbRequest.REMOVE_MANSION
                );

            dispatch(setSingleMansion(deleteMansion));
            await LoadMansions()(dispatch);

            callback?.();
        } catch {}
    };
}

export function UpdateMansion(mansion: Mansion) {
    return async (dispatch: PbDispatch) => {
        try {
            const request: RequestInit = {
                method: 'POST',
                body: JSON.stringify(mansion),
            };

            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/mansions/${mansion.id}/${ACCOUNTING_AREA_ID_MARKER}`,
                request,
                PbRequest.UPDATE_MANSION
            );

            dispatch(setSingleMansion(mansion));
        } catch {}
    };
}

export default mansionsSlice.reducer;
